import { basicApi } from '../utils/api';

export type KakaoMessageType = 'BA' | 'EX' | 'AD' | 'MI';
export type KakaoEmphasizeType = 'NONE' | 'TEXT' | 'IMAGE' | 'ITEM_LIST';
export type KakaoAssetPurpose = 'TEMPLATE_IMAGE' | 'HIGHLIGHT_IMAGE' | 'KAKAO_IMAGE';

export interface KakaoButton {
  buttonType: string;
  buttonName: string;
  linkMo?: string;
  linkPc?: string;
  linkAnd?: string;
  linkIos?: string;
  chatExtra?: string;
  targetOut?: boolean;
}

export interface KakaoQuickReply {
  linkType: string;
  name: string;
  linkMo?: string;
  linkPc?: string;
  linkAnd?: string;
  linkIos?: string;
  chatExtra?: string;
  targetOut?: boolean;
}

export interface KakaoHighlight { title?: string; description?: string; imageId?: string }
export interface KakaoItemLine { title?: string; description?: string }
export interface KakaoItem { list?: KakaoItemLine[]; summary?: KakaoItemLine }
export interface KakaoComment { memberId?: string; content?: string; dateCreated?: string }
export interface KakaoTemplateCode { code?: string; status?: string; dormant?: boolean; outDated?: boolean }

export interface KakaoTemplateBody {
  channelId?: string;
  channelGroupId?: string;
  name: string;
  content: string;
  categoryCode: string;
  messageType?: KakaoMessageType;
  emphasizeType?: KakaoEmphasizeType;
  buttons?: KakaoButton[];
  quickReplies?: KakaoQuickReply[];
  header?: string;
  highlight?: KakaoHighlight;
  item?: KakaoItem;
  extra?: string;
  ad?: string;
  emphasizeTitle?: string;
  emphasizeSubtitle?: string;
  securityFlag?: boolean;
  imageId?: string;
}

export interface KakaoTemplate extends KakaoTemplateBody {
  templateId: string;
  status?: string;
  unavailable: boolean;
  comments?: KakaoComment[];
  variables?: Array<string | { name: string }>;
  codes?: KakaoTemplateCode[];
  isHidden?: boolean;
  isDeleted?: boolean;
  dateCreated?: string;
  dateUpdated?: string;
}

export interface KakaoCategory { code: string; name: string; groupName?: string; inclusion?: string; exclusion?: string }
export interface KakaoChannel { channelId: string; channelName?: string }
export interface KakaoChannelGroup { channelGroupId: string; name?: string; status?: string; channelIds: string[] }
export interface KakaoAsset {
  fileId: string;
  type: string;
  name?: string;
  originalName?: string;
  url?: string;
  link?: string;
  width?: number;
  height?: number;
  fileSize?: number;
}
export interface KakaoPage<T> { content: T[]; totalElements: number; totalPages: number; number: number; size: number }
export interface PageParams { pg: number; limit: number }

const templatesPath = '/solapi/kakao-templates';
const templatePath = (id: string) => `${templatesPath}/${encodeURIComponent(id)}`;

export const listKakaoTemplates = async (params: PageParams) =>
  (await basicApi.get<KakaoPage<KakaoTemplate>>(templatesPath, { params })).data;

export const getKakaoTemplate = async (id: string) =>
  (await basicApi.get<KakaoTemplate>(templatePath(id))).data;

export const createKakaoTemplate = async (body: KakaoTemplateBody) =>
  (await basicApi.post<KakaoTemplate>(templatesPath, body)).data;

export const updateKakaoTemplate = async (id: string, body: Pick<KakaoTemplateBody, 'name' | 'content' | 'categoryCode'>) =>
  (await basicApi.put<KakaoTemplate>(templatePath(id), body)).data;

export const patchKakaoTemplate = async (id: string, changes: Record<string, unknown>) =>
  (await basicApi.patch<KakaoTemplate>(templatePath(id), { changes })).data;

export const updateKakaoTemplateName = async (id: string, name: string) =>
  (await basicApi.put<KakaoTemplate>(`${templatePath(id)}/name`, { name })).data;

export const deleteKakaoTemplate = async (id: string) =>
  (await basicApi.delete(templatePath(id))).data;

export const requestKakaoInspection = async (id: string, comment?: string) =>
  (await (comment
    ? basicApi.post<KakaoTemplate>(`${templatePath(id)}/inspection`, { comment })
    : basicApi.post<KakaoTemplate>(`${templatePath(id)}/inspection`))).data;

export const cancelKakaoInspection = async (id: string) =>
  (await basicApi.delete<KakaoTemplate>(`${templatePath(id)}/inspection`)).data;

export const reactivateKakaoTemplate = async (id: string) =>
  (await basicApi.post<KakaoTemplate>(`${templatePath(id)}/reactivation`)).data;

export const listKakaoCategories = async (params: PageParams) =>
  (await basicApi.get<KakaoPage<KakaoCategory>>(`${templatesPath}/categories`, { params })).data;

export const listKakaoChannels = async (params: PageParams) =>
  (await basicApi.get<KakaoPage<KakaoChannel>>('/solapi/channels', { params })).data;

export const listAllKakaoChannels = async (): Promise<KakaoChannel[]> => {
  const first = await listKakaoChannels({ pg: 1, limit: 100 });
  const channels = [...first.content];
  for (let pg = 2; pg <= first.totalPages; pg += 1) {
    const next = await listKakaoChannels({ pg, limit: 100 });
    channels.push(...next.content);
  }
  return channels;
};

export const listKakaoChannelGroups = async (params: PageParams) =>
  (await basicApi.get<KakaoPage<KakaoChannelGroup>>('/solapi/channels/groups', { params })).data;

export const listAllKakaoChannelGroups = async (): Promise<KakaoChannelGroup[]> => {
  const first = await listKakaoChannelGroups({ pg: 1, limit: 100 });
  const groups = [...first.content];
  for (let pg = 2; pg <= first.totalPages; pg += 1) {
    const next = await listKakaoChannelGroups({ pg, limit: 100 });
    groups.push(...next.content);
  }
  return groups;
};

export const listKakaoAssets = async (params: PageParams) =>
  (await basicApi.get<KakaoPage<KakaoAsset>>('/solapi/kakao-assets', { params })).data;

export const getKakaoAsset = async (fileId: string) =>
  (await basicApi.get<KakaoAsset>(`/solapi/kakao-assets/${encodeURIComponent(fileId)}`)).data;

export const uploadKakaoAsset = async (file: File, purpose: KakaoAssetPurpose) => {
  const body = new FormData();
  body.append('file', file);
  return (await basicApi.post<KakaoAsset>('/solapi/kakao-assets', body, {
    params: { purpose }, headers: { 'Content-Type': 'multipart/form-data' },
  })).data;
};
